import { useMemo } from 'react'
import { Grid } from '@mui/material'
import { AnimatedBox, DashboardHeader, LoadingState, StatCard, QuickActions } from './index'

/**
 * DashboardLayout component - Reusable layout for all dashboard pages
 * Eliminates duplication across Admin, Client, and Influencer dashboards
 * 
 * @param {string} title - Dashboard title
 * @param {Array} statCards - Array of stat card configurations
 * @param {Array} quickActions - Array of quick action configurations
 * @param {boolean} loading - Loading state
 * @param {ReactNode} children - Additional content to render below stats
 * @param {ReactNode} header - Custom header (optional, defaults to DashboardHeader)
 */
export const DashboardLayout = ({
  title,
  statCards = [],
  quickActions = [],
  loading = false,
  children = null,
  header = null,
}) => {
  const defaultGridSize = useMemo(() => {
    // Determine grid size based on number of cards
    if (statCards.length === 4) return { xs: 12, sm: 6, md: 3 }
    if (statCards.length === 3) return { xs: 12, sm: 6, md: 4 }
    if (statCards.length === 2) return { xs: 12, sm: 6 }
    return { xs: 12, sm: 6, md: 4 } // Default
  }, [statCards.length])

  return (
    <AnimatedBox>
      {header || <DashboardHeader title={title} />}

      {loading ? (
        <LoadingState />
      ) : (
        <>
          {statCards.length > 0 && (
            <Grid container spacing={4} sx={{ mb: 4 }}>
              {statCards.map((card, index) => (
                <Grid size={card.gridSize || defaultGridSize} key={card.title || index}>
                  <StatCard {...card} index={index} />
                </Grid>
              ))}
            </Grid>
          )}

          {quickActions.length > 0 && <QuickActions actions={quickActions} />}

          {children}
        </>
      )}
    </AnimatedBox>
  )
}
